'use client';

import clsx from 'clsx';
import { FC } from 'react';

import { CarComparisonAdd } from 'src/car-comparison/components/CarComparisonAdd/CarComparisonAdd';
import { Icon } from 'src/general/Icons/Icon';
import { Link } from 'src/general/components/Link/Link';
import { ProductImage } from 'src/general/components/ProductImage/ProductImage';
import { Shade } from 'src/general/components/Shade/Shade';
import { formatNumber } from 'src/general/helpers/formatNumber';
import { BaseCarData } from 'src/types/Car.types';

import { AddToWatchListButton } from '../AddToWatchList/AddToWatchListButton';
import { AddToWatchListIcon } from '../AddToWatchList/AddToWatchListIcon';
import { StatusPill } from '../StatusPill/StatusPill';
import { ProductCardRepaymentsTooltip } from './ProductCardRepaymentsTooltip';

type props = {
  carData: BaseCarData;
  weeklyRepayment: number;
  defaultRate: number;
  imagePriority?: boolean;
  onCardClick?: () => void;
  onWatchlistStatusChange?: () => Promise<void>;
  imageTestId?: string;
} & (
  | {
      variant: 'plp';
    }
  | {
      variant: 'default';
    }
);

export const ProductCard: FC<props> = ({
  carData,
  weeklyRepayment,
  defaultRate,
  imagePriority,
  onWatchlistStatusChange,
  onCardClick,
  imageTestId,
  ...props
}) => {
  const priceString = formatNumber(carData.price, { prepend: '$' });
  const dutyString = formatNumber(carData.dutyPrice, { prepend: '$' });
  const weeklyRepaymentString = formatNumber(weeklyRepayment, { prepend: '$' });
  const defaultRateString = formatNumber(defaultRate * 100, { decimals: 2, trimZeroDecimals: true, append: '%' });

  return (
    <div
      className={clsx(
        'relative isolate overflow-hidden rounded-lg bg-for-neu-tie1 shadow-elevation-2dp',
        'box-border h-full border border-bor-neu-mut',
        {
          'focus-within:shadow-elevation-12dp hover:shadow-elevation-12dp': props.variant === 'plp',
        },
      )}
      data-testid={`ProductCard-container-${carData.sku}`}
    >
      <div className="relative p-3">
        <Link
          href={`/product-detail/${carData.sku}`}
          onClick={onCardClick}
          className="c-shade_container c-shade_container--grey absolute inset-0 z-10 cursor-pointer overflow-hidden rounded-t-lg"
          data-testid="used-car-product-card"
        >
          <Shade noBackground />
          <span className="sr-only">{carData.model}</span>
        </Link>
        <ProductImage
          key={carData.sku}
          src={carData.imageUrl}
          alt={carData.model}
          className="col-span-2 mb-4 h-auto w-full rounded-xs"
          imagePriority={imagePriority}
          data-testid={imageTestId}
        />
        <div className="absolute top-5 left-5">
          <StatusPill sku={carData.sku} state={carData.state} />
        </div>
        <div className="grid items-start">
          <p
            className="c-fs-body1 col-start-1 pb-0.5 pl-1 font-extrabold uppercase"
            data-testid="ProductCard-component-titleText"
          >
            {carData.model}
          </p>
          <p
            className="c-fs-body2 col-start-1 pb-0.5 pl-1 text-tex-def-tie2 uppercase"
            data-testid="ProductCard-component-variantText"
          >
            {carData.variant}
          </p>
          <p className="c-fs-body2 col-start-1 pb-3 pl-1 text-tex-def-tie2">
            <span data-testid="ProductCard-component-distanceText">
              {formatNumber(carData.kilometers, { append: ' km' })}
            </span>
            <span>&ensp;•&ensp;</span>
            <span data-testid="ProductCard-component-transmissionText">{carData.transmission}</span>
          </p>

          {props.variant === 'default' && (
            <aside className="relative z-20 col-start-2 row-span-3 row-start-1 justify-self-end pr-1">
              <AddToWatchListIcon
                key={carData.sku}
                sku={carData.sku}
                product={{
                  ...carData,
                  title: carData.model,
                  price: priceString,
                  dutyPrice: dutyString,
                }}
                onWatchlistStatusChange={onWatchlistStatusChange}
                variant="productCard"
              />
            </aside>
          )}
        </div>
        {carData.watchers ? (
          <div className="mb-4 flex w-fit items-center justify-start gap-x-1 rounded-2xs bg-for-neu-tie2 px-2 py-1">
            <Icon name="watching-people" />
            <span className="c-fs-caption2">
              <b>{carData.watchers} others</b> watching this car
            </span>
          </div>
        ) : null}
        <p className="c-fs-h5 pb-0.5 pl-1 font-extrabold" data-testid="ProductCard-component-priceText">
          {priceString}
        </p>
        <div
          className="c-fs-caption2 z-30 pb-0.5 pl-1 text-tex-def-tie2"
          data-testid="ProductCard-component-repaymentText"
        >
          <span>
            est. <strong>{weeklyRepaymentString}/wk</strong> based on <strong>{defaultRateString}</strong> p.a.
          </span>{' '}
          <ProductCardRepaymentsTooltip weeklyRepayment={weeklyRepaymentString} defaultRate={defaultRateString} />
        </div>
      </div>

      {props.variant !== 'default' && (
        <footer className="flex flex-col justify-end">
          <aside className="grid h-12 grid-cols-2 border-t border-bor-neu-bas">
            <CarComparisonAdd
              sku={carData.sku}
              className="c-shade_container c-shade_container--grey min-h-12 rounded-none! border-t-0! border-r! border-b-0! border-l-0! border-bor-neu-mut! font-normal! text-inherit!"
            />
            <AddToWatchListButton
              key={carData.sku}
              className="c-shade_container c-shade_container--grey min-h-12 rounded-none! border-none! font-normal! text-inherit!"
              sku={carData.sku}
              product={{
                ...carData,
                title: carData.model,
                price: priceString,
                dutyPrice: dutyString,
              }}
              onWatchlistStatusChange={onWatchlistStatusChange}
              buttonProps={{
                variant: 'secondary',
                fullWidth: true,
              }}
            />
          </aside>
        </footer>
      )}
    </div>
  );
};
